import { useEffect, useState } from 'react';
import { useStore, Position, useEdges } from 'reactflow';
import { Box } from '@mui/material';
import CustomMovableHandle from '../../CustomMovableHandle';
import NodeToolbar from '../../NodeToolbar';
import TacacsIcon from '@shared/static/nodes/TacacsIcon';
import { NODE_DEFAULT_PORTS, NODE_SIZES } from '@pages/schemes/constants';
import useRotation from '@pages/schemes/hooks/useRotation';
import useSelectionStyle from '@pages/schemes/hooks/useSelectionStyle';
import useDynamicHandlePosition from '@pages/schemes/hooks/useDynamicHandlePosition';
import HiddenNodeNameInput from '@pages/schemes/components/Flow/nodes/HiddenNodeNameInput';

const HANDLES_POOL = ['port0', 'port1'];

const TacacsNode = (props) => {
    const { id, data, selected, isConnectable } = props;
    const isNodeDraggable = useStore((state) => state.nodesDraggable);
    // eslint-disable-next-line no-unused-vars
    const [_rotateControlRef, rotation] = useRotation(data, id);
    const selectionStyle = useSelectionStyle(selected);
    const [selectedHandleId, setSelectedHandleId] = useState(data.selectedHandleId);
    const [handle_0_Position, setInitialHandle_0_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[0],
        Position.Top,
    );
    const [handle_1_Position, setInitialHandle_1_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[1],
        Position.Right,
    );

    const mapHandleIdToUseDynamicHandlePosition = {
        [HANDLES_POOL[0]]: [handle_0_Position, setInitialHandle_0_Position],
        [HANDLES_POOL[1]]: [handle_1_Position, setInitialHandle_1_Position],
    };

    const edges = useEdges();

    const nodeIsConnected = edges.some((edge) => edge.source === id && edge.sourceHandle === HANDLES_POOL[1]);

    useEffect(() => {
        if (!nodeIsConnected) {
            data.ports[1].ips = NODE_DEFAULT_PORTS.tacacs[1].ips;
        }
    }, [data, nodeIsConnected]);

    const handleUpdateUsersPoolSize = (newValue) => {
        data['users-pool-size'] = newValue;
    };

    return (
        <Box
            sx={{
                ...selectionStyle,
                maxWidth: NODE_SIZES['tacacs'].width,
                maxHeight: NODE_SIZES['tacacs'].height,
                transform: `rotate(${rotation}deg)`,
                padding: '2px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <TacacsIcon />
            <CustomMovableHandle
                nodeType="tacacs"
                nodeId={id}
                nodeSelected={selected}
                selectedHandleId={selectedHandleId}
                setSelectedHandleId={setSelectedHandleId}
                handleId={HANDLES_POOL[0]}
                position={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[0]][0]}
                setInitialHandlePosition={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[0]][1]}
                isConnectable={isConnectable}
                data={data}
                label={HANDLES_POOL[0]}
                movable={isNodeDraggable}
            />
            <CustomMovableHandle
                nodeType="tacacs"
                nodeId={id}
                nodeSelected={selected}
                selectedHandleId={selectedHandleId}
                setSelectedHandleId={setSelectedHandleId}
                handleId={HANDLES_POOL[1]}
                position={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[1]][0]}
                setInitialHandlePosition={mapHandleIdToUseDynamicHandlePosition[HANDLES_POOL[1]][1]}
                isConnectable={isConnectable}
                data={data}
                label={HANDLES_POOL[1]}
                movable={isNodeDraggable}
            />
            {selected && (
                <NodeToolbar
                    HANDLES_POOL={HANDLES_POOL}
                    hidden={!selected}
                    nodeId={id}
                    NodeConfigMenuProps={{
                        withStaticPorts: true,
                        configureOnly: !isNodeDraggable,
                        ipsReadonly: !nodeIsConnected,
                        nodesParamsCustomisable: true,
                        handleUpdateUsersPoolSize,
                    }}
                />
            )}
            <HiddenNodeNameInput nodeData={data} nodeId={id} nodeSelected={selected} />
        </Box>
    );
};

export default TacacsNode;
